/**
 * Attributes the Storyblok Preview Bridge looks for when resolving a click to a
 * story block. Mirrors what `@storyblok/js`'s `storyblokEditable()` helper
 * returns, so the bridge treats our markup exactly like a first-party
 * integration's.
 */
export interface IStoryblokEditableAttributes {
    'data-blok-c': string;
    'data-blok-uid': string;
}

const PREFIX = '<!--#storyblok#';
const SUFFIX = '-->';

/**
 * Turns the backend's raw Storyblok `_editable` comment into the two `data-blok-*`
 * attributes the bridge resolves at click time.
 *
 * The backend hands us the comment verbatim (see `PageComponentBase.StoryblokEditable`),
 * e.g.
 *
 * ```
 * <!--#storyblok#{"name": "media-v2", "space": "169662", "uid": "2a41...", "id": "1609..."}-->
 * ```
 *
 * We deliberately do *not* insert that comment into the DOM. The bridge only scans
 * for comments once, on `enterEditmode`, and binds to `comment.nextElementSibling`;
 * because this app has no client hydration, the browser re-renders over the SSR DOM
 * and any comment present at scan time is thrown away. The attribute form is resolved
 * live on every click instead, so it survives re-renders.
 *
 * Returns `null` — never throws, never logs — for anything unparseable. Outside
 * preview mode the field is simply absent, which is the common case.
 */
export function parseStoryblokEditable(raw: string | null | undefined): IStoryblokEditableAttributes | null {
    const trimmed = raw?.trim();
    if (!trimmed?.startsWith(PREFIX) || !trimmed.endsWith(SUFFIX)) {
        return null;
    }

    try {
        const options: unknown = JSON.parse(trimmed.slice(PREFIX.length, -SUFFIX.length));
        if (!isEditableOptions(options)) {
            return null;
        }

        return {
            'data-blok-c': JSON.stringify(options),
            'data-blok-uid': `${options.id}-${options.uid}`,
        };
    } catch {
        return null;
    }
}

function isEditableOptions(value: unknown): value is { id: string; uid: string } {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const { id, uid } = value as { id?: unknown; uid?: unknown };

    return typeof id === 'string' && id.length > 0 && typeof uid === 'string' && uid.length > 0;
}
